/**
 * Per-case chat state, keyed by *conversation* (chat thread). Each case can
 * hold many conversations; the Ask page lets the user start a new one, jump
 * back into older ones, rename them, delete them, and search across them.
 *
 * The store survives:
 *   - tab switches inside CaseView (Ask / Timeline / Documents)
 *   - full-page navigations away from the case (Dashboard and back)
 *
 * Per case we keep:
 *   - conversations: sidebar list (recent first), with summary stats
 *   - activeId:       the currently-selected conversation, or `null` for a
 *                     fresh "new chat" composer that hasn't been saved yet
 *   - threads:        per-conversation transcript + composer state
 *   - newDraft/Mode:  composer state for the not-yet-created "new chat"
 *   - search:         current sidebar search box value
 *
 * Persistence: conversations and turns come from the server. We rehydrate
 * lazily — listing conversations on first access for a case, and loading
 * each conversation's queries on first selection.
 */
import { create } from "zustand";
import {
  api,
  ChatScope,
  Conversation,
  QueryStatusResponse,
  QuerySubmitResponse,
  QueryStatus,
  TimelineEvent,
  Citation,
} from "./api";
export type ChatMode = "qa" | "timeline" | "both" | "insights";

export interface ChatTurn {
  query_id: number;
  question: string;
  mode: ChatMode;
  status: QueryStatus;
  progress_step?: string | null;
  progress_pct: number;
  queue_position: number;
  answer?: string | null;
  timeline: TimelineEvent[];
  citations: Citation[];
  latency_ms?: number | null;
  error_message?: string | null;
  created_at: string;
}

interface Thread {
  turns: ChatTurn[];
  draft: string;
  mode: ChatMode;
  hydrated: boolean;
}

interface CaseChat {
  /** Threads keyed by scope so the Mine/Shared tabs each have their own list. */
  conversationsByScope: Record<ChatScope, Conversation[]>;
  scope: ChatScope;
  activeId: number | null;       // null = "new chat" (composer not saved yet)
  threads: Record<number, Thread>;
  newDraft: string;
  newMode: ChatMode;
  search: string;
  /** Per-scope load flag so switching tabs only refetches when first opened. */
  conversationsLoadedByScope: Record<ChatScope, boolean>;
  pollerHandle: number | null;
  pollerRefcount: number;
  // True when the user explicitly clicked "New chat". Suppresses the
  // auto-select-most-recent behavior so the empty composer actually shows.
  // Cleared once they pick an existing conversation or actually send a
  // question (which materializes the new conversation).
  intentNewChat: boolean;
}

interface ChatStore {
  byCase: Record<number, CaseChat>;

  /** Clear all in-memory chat data when the authenticated user changes. */
  reset: () => void;

  ensure: (caseId: number) => CaseChat;

  /** Pull (or refresh) the conversations for one scope (defaults to current). */
  loadConversations: (caseId: number, search?: string, scope?: ChatScope) => Promise<void>;

  /** Set the search query AND reload the active scope with that filter. */
  setSearch: (caseId: number, search: string) => void;

  /** Switch the visible sidebar tab (Mine / Shared). */
  setScope: (caseId: number, scope: ChatScope) => void;

  /** Switch to a "fresh chat" composer with no conversation_id yet. */
  newChat: (caseId: number) => void;

  /** Select an existing conversation and (lazily) hydrate its turns. */
  selectConversation: (caseId: number, convId: number) => Promise<void>;

  /** Edit the composer for whichever conversation is active. */
  setDraft: (caseId: number, draft: string) => void;
  setMode: (caseId: number, mode: ChatMode) => void;

  /** Submit a question against the active conversation (creates one if needed). */
  ask: (caseId: number, question: string, mode: ChatMode) => Promise<void>;

  /** Pull the latest history for a single conversation. Idempotent. */
  hydrateConversation: (caseId: number, convId: number) => Promise<void>;

  renameConversation: (caseId: number, convId: number, title: string) => Promise<void>;
  deleteConversation: (caseId: number, convId: number) => Promise<void>;

  /** Owner-only: flip a chat between private and shared-with-the-case. */
  setShared: (caseId: number, convId: number, shared: boolean) => Promise<void>;

  /** Mark a component as wanting live updates for this case; reference-counted. */
  startPolling: (caseId: number) => void;
  stopPolling: (caseId: number) => void;
}

const POLL_INTERVAL_MS = 1500;

function emptyCase(): CaseChat {
  return {
    conversationsByScope: { mine: [], shared: [], all: [] },
    scope: "mine",
    activeId: null,
    threads: {},
    newDraft: "",
    newMode: "both",
    search: "",
    conversationsLoadedByScope: { mine: false, shared: false, all: false },
    pollerHandle: null,
    pollerRefcount: 0,
    intentNewChat: false,
  };
}

function emptyThread(): Thread {
  return { turns: [], draft: "", mode: "both", hydrated: false };
}

function turnFromStatus(s: QueryStatusResponse): ChatTurn {
  return {
    query_id: s.query_id,
    question: s.question,
    mode: s.mode,
    status: s.status,
    progress_step: s.progress_step,
    progress_pct: s.progress_pct,
    queue_position: s.queue_position,
    answer: s.answer,
    timeline: s.timeline ?? [],
    citations: s.citations ?? [],
    latency_ms: s.latency_ms ?? null,
    error_message: s.error_message ?? null,
    created_at: s.created_at,
  };
}

function patchCase(
  set: Setter,
  caseId: number,
  patch: Partial<CaseChat> | ((c: CaseChat) => Partial<CaseChat>),
) {
  set((st) => {
    const cur = st.byCase[caseId];
    if (!cur) return {};
    const delta = typeof patch === "function" ? patch(cur) : patch;
    return { byCase: { ...st.byCase, [caseId]: { ...cur, ...delta } } };
  });
}

export const useChatStore = create<ChatStore>((set, get) => ({
  byCase: {},

  reset: () => {
    for (const c of Object.values(get().byCase)) {
      if (c.pollerHandle != null) window.clearInterval(c.pollerHandle);
    }
    set({ byCase: {} });
  },

  ensure: (caseId) => {
    const existing = get().byCase[caseId];
    if (existing) return existing;
    const fresh = emptyCase();
    set((st) => ({ byCase: { ...st.byCase, [caseId]: fresh } }));
    return fresh;
  },

  loadConversations: async (caseId, search, scope) => {
    get().ensure(caseId);
    const cur = get().byCase[caseId];
    const targetScope: ChatScope = scope ?? cur.scope;
    const q = (search ?? cur.search).trim();
    const params = new URLSearchParams({ scope: targetScope });
    if (q) params.set("q", q);
    const path = `/cases/${caseId}/conversations?${params.toString()}`;
    try {
      const rows = await api.get<Conversation[]>(path);
      patchCase(set, caseId, (c) => ({
        conversationsByScope: { ...c.conversationsByScope, [targetScope]: rows },
        conversationsLoadedByScope: { ...c.conversationsLoadedByScope, [targetScope]: true },
      }));
    } catch {
      patchCase(set, caseId, (c) => ({
        conversationsLoadedByScope: { ...c.conversationsLoadedByScope, [targetScope]: true },
      }));
    }
  },

  setSearch: (caseId, search) => {
    get().ensure(caseId);
    patchCase(set, caseId, { search });
    // Re-query whichever tab is currently visible.
    void get().loadConversations(caseId, search);
  },

  setScope: (caseId, scope) => {
    get().ensure(caseId);
    patchCase(set, caseId, { scope });
    const c = get().byCase[caseId];
    // Lazy-load on first visit; switching back to a tab we've already
    // loaded keeps the snappy in-memory snapshot.
    if (!c.conversationsLoadedByScope[scope]) {
      void get().loadConversations(caseId, undefined, scope);
    }
  },

  newChat: (caseId) => {
    get().ensure(caseId);
    // A fresh chat is always one of "mine" — flip the tab back so the user
    // isn't staring at the Shared list while composing their question.
    patchCase(set, caseId, { activeId: null, intentNewChat: true, scope: "mine" });
  },

  selectConversation: async (caseId, convId) => {
    get().ensure(caseId);
    patchCase(set, caseId, (c) => ({
      activeId: convId,
      intentNewChat: false,
      threads: c.threads[convId] ? c.threads : { ...c.threads, [convId]: emptyThread() },
    }));
    const t = get().byCase[caseId].threads[convId];
    if (!t || !t.hydrated) {
      await get().hydrateConversation(caseId, convId);
    }
    // Make sure the poller is alive; if there are still active turns it'll
    // pick them up immediately.
    ensurePollerRunning(caseId, set, get);
  },

  setDraft: (caseId, draft) => {
    get().ensure(caseId);
    patchCase(set, caseId, (c) => {
      if (c.activeId == null) return { newDraft: draft };
      const t = c.threads[c.activeId] ?? emptyThread();
      return { threads: { ...c.threads, [c.activeId]: { ...t, draft } } };
    });
  },

  setMode: (caseId, mode) => {
    get().ensure(caseId);
    patchCase(set, caseId, (c) => {
      if (c.activeId == null) return { newMode: mode };
      const t = c.threads[c.activeId] ?? emptyThread();
      return { threads: { ...c.threads, [c.activeId]: { ...t, mode } } };
    });
  },

  ask: async (caseId, question, mode) => {
    get().ensure(caseId);
    const cur = get().byCase[caseId];
    const explicitConvId = cur.activeId; // null => server creates one

    let resp: QuerySubmitResponse;
    try {
      resp = await api.post<QuerySubmitResponse>(`/cases/${caseId}/query`, {
        question,
        mode,
        top_k: 12,
        ...(explicitConvId != null ? { conversation_id: explicitConvId } : {}),
      });
    } catch (e: any) {
      const errorTurn: ChatTurn = {
        query_id: -Date.now(),
        question,
        mode,
        status: "failed",
        progress_pct: 100,
        queue_position: -1,
        answer: null,
        timeline: [],
        citations: [],
        error_message: e?.message ?? "Failed to submit",
        created_at: new Date().toISOString(),
      };
      // Drop the error onto the active thread, or under a synthetic
      // placeholder if there isn't one yet.
      patchCase(set, caseId, (c) => {
        if (c.activeId == null) {
          return { newDraft: question };
        }
        const t = c.threads[c.activeId] ?? emptyThread();
        return {
          threads: {
            ...c.threads,
            [c.activeId]: { ...t, turns: [...t.turns, errorTurn] },
          },
        };
      });
      return;
    }

    const optimistic: ChatTurn = {
      query_id: resp.query_id,
      question,
      mode,
      status: resp.status,
      progress_step: resp.status === "queued" ? "Waiting in queue" : "Starting",
      progress_pct: resp.status === "queued" ? 0 : 5,
      queue_position: resp.queue_position,
      timeline: [],
      citations: [],
      created_at: new Date().toISOString(),
    };

    const convId = resp.conversation_id;
    patchCase(set, caseId, (c) => {
      const isNew = explicitConvId == null;
      const existingThread = c.threads[convId] ?? emptyThread();
      const nextThread: Thread = {
        ...existingThread,
        turns: [...existingThread.turns, optimistic],
        // a newly-created conversation has no server history to fetch.
        hydrated: existingThread.hydrated || isNew,
        mode,
      };
      // If this was a brand-new chat, optimistically inject it into the
      // user's "Mine" sidebar so they immediately see what they just
      // started. Brand-new chats are private-by-default.
      const synthConv: Conversation | null = isNew
        ? {
            id: convId,
            case_id: caseId,
            title: deriveTitle(question),
            is_shared: false,
            is_owner: true,
            message_count: 1,
            last_question: question,
            last_message_at: new Date().toISOString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }
        : null;

      const updateRow = (x: Conversation): Conversation =>
        x.id === convId
          ? {
              ...x,
              message_count: (x.message_count ?? 0) + 1,
              last_question: question,
              last_message_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }
          : x;

      const nextByScope = mapScopes(c.conversationsByScope, (rows, scope) => {
        if (synthConv && scope === "mine") {
          return [synthConv, ...rows.filter((x) => x.id !== convId)];
        }
        if (synthConv && scope === "all") {
          return [synthConv, ...rows.filter((x) => x.id !== convId)];
        }
        return rows.map(updateRow);
      });

      return {
        activeId: convId,
        intentNewChat: false,
        newDraft: isNew ? "" : c.newDraft,
        threads: { ...c.threads, [convId]: nextThread },
        conversationsByScope: nextByScope,
      };
    });

    ensurePollerRunning(caseId, set, get);
    // Refresh the sidebar shortly so titles/timestamps come from the server
    // (server may have set the title from the first question).
    void get().loadConversations(caseId);
  },

  hydrateConversation: async (caseId, convId) => {
    get().ensure(caseId);
    try {
      const rows = await api.get<QueryStatusResponse[]>(
        `/cases/${caseId}/queries?conversation_id=${convId}&limit=200`,
      );
      const sorted = [...rows].sort((a, b) =>
        a.created_at.localeCompare(b.created_at),
      );
      const turns = sorted.map(turnFromStatus);
      patchCase(set, caseId, (c) => {
        const t = c.threads[convId] ?? emptyThread();
        return {
          threads: {
            ...c.threads,
            [convId]: { ...t, turns, hydrated: true },
          },
        };
      });
      if (turns.some((t) => isActive(t.status))) {
        ensurePollerRunning(caseId, set, get);
      }
    } catch {
      patchCase(set, caseId, (c) => {
        const t = c.threads[convId] ?? emptyThread();
        return {
          threads: { ...c.threads, [convId]: { ...t, hydrated: true } },
        };
      });
    }
  },

  renameConversation: async (caseId, convId, title) => {
    const trimmed = title.trim();
    patchCase(set, caseId, (c) => ({
      conversationsByScope: mapScopes(c.conversationsByScope, (rows) =>
        rows.map((x) => (x.id === convId ? { ...x, title: trimmed || null } : x)),
      ),
    }));
    try {
      const updated = await api.patch<Conversation>(
        `/cases/${caseId}/conversations/${convId}`,
        { title: trimmed },
      );
      patchCase(set, caseId, (c) => ({
        conversationsByScope: mapScopes(c.conversationsByScope, (rows) =>
          rows.map((x) => (x.id === convId ? updated : x)),
        ),
      }));
    } catch {
      // best-effort: refresh from server to get true state
      void get().loadConversations(caseId);
    }
  },

  deleteConversation: async (caseId, convId) => {
    try {
      await api.del<void>(`/cases/${caseId}/conversations/${convId}`);
    } catch {
      // ignore — refresh will reconcile
    }
    patchCase(set, caseId, (c) => {
      const { [convId]: _drop, ...threads } = c.threads;
      const conversationsByScope = mapScopes(c.conversationsByScope, (rows) =>
        rows.filter((x) => x.id !== convId),
      );
      // If we just nuked the active thread, fall back to the next most
      // recent conversation in the visible scope, or the "new chat"
      // composer if none remain there.
      const visible = conversationsByScope[c.scope];
      const activeId =
        c.activeId === convId ? (visible[0]?.id ?? null) : c.activeId;
      return { threads, conversationsByScope, activeId };
    });
    // If the new active is a conversation we haven't loaded yet, hydrate it.
    const after = get().byCase[caseId];
    if (after.activeId != null && !after.threads[after.activeId]?.hydrated) {
      void get().hydrateConversation(caseId, after.activeId);
    }
  },

  setShared: async (caseId, convId, shared) => {
    patchCase(set, caseId, (c) => ({
      conversationsByScope: mapScopes(c.conversationsByScope, (rows) =>
        rows.map((x) => (x.id === convId ? { ...x, is_shared: shared } : x)),
      ),
    }));
    try {
      const updated = await api.patch<Conversation>(
        `/cases/${caseId}/conversations/${convId}`,
        { is_shared: shared },
      );
      patchCase(set, caseId, (c) => ({
        conversationsByScope: mapScopes(c.conversationsByScope, (rows) =>
          rows.map((x) => (x.id === convId ? updated : x)),
        ),
      }));
      // Other tabs (especially "shared") may now contain new entries (or
      // need an entry removed). Mark them stale so a tab switch re-fetches.
      patchCase(set, caseId, (c) => ({
        conversationsLoadedByScope: {
          ...c.conversationsLoadedByScope,
          shared: false,
          all: false,
        },
      }));
    } catch {
      void get().loadConversations(caseId);
    }
  },

  startPolling: (caseId) => {
    get().ensure(caseId);
    patchCase(set, caseId, (c) => ({ pollerRefcount: c.pollerRefcount + 1 }));
    ensurePollerRunning(caseId, set, get);
  },

  stopPolling: (caseId) => {
    const cur = get().byCase[caseId];
    if (!cur) return;
    patchCase(set, caseId, (c) => ({
      pollerRefcount: Math.max(0, c.pollerRefcount - 1),
    }));
    // We DON'T tear the poller down even when refcount hits 0 — the poller
    // self-stops when no turns are active anymore. That way a question
    // submitted from the chat tab keeps updating after the user leaves.
  },
}));

function isActive(s: QueryStatus): boolean {
  return s === "queued" || s === "running" || s === "pending";
}

/** Apply the same transform across every scope bucket. */
function mapScopes(
  byScope: Record<ChatScope, Conversation[]>,
  fn: (rows: Conversation[], scope: ChatScope) => Conversation[],
): Record<ChatScope, Conversation[]> {
  return {
    mine:   fn(byScope.mine,   "mine"),
    shared: fn(byScope.shared, "shared"),
    all:    fn(byScope.all,    "all"),
  };
}

function deriveTitle(question: string, max = 80): string {
  const cleaned = question.trim().replace(/\s+/g, " ");
  return cleaned.length <= max ? cleaned : cleaned.slice(0, max - 1) + "…";
}

type Setter = (
  fn: (st: { byCase: Record<number, CaseChat> }) => Partial<{
    byCase: Record<number, CaseChat>;
  }>,
) => void;
type Getter = () => { byCase: Record<number, CaseChat> };

function ensurePollerRunning(caseId: number, set: Setter, get: Getter) {
  const cur = get().byCase[caseId];
  if (!cur || cur.pollerHandle != null) return;

  const handle = window.setInterval(() => tick(caseId, set, get), POLL_INTERVAL_MS);
  patchCase(set, caseId, { pollerHandle: handle });
  void tick(caseId, set, get);
}

async function tick(caseId: number, set: Setter, get: Getter) {
  const cur = get().byCase[caseId];
  if (!cur) return;

  const activeIds: number[] = [];
  for (const t of Object.values(cur.threads)) {
    for (const turn of t.turns) {
      if (isActive(turn.status) && turn.query_id > 0) activeIds.push(turn.query_id);
    }
  }

  if (activeIds.length === 0) {
    if (cur.pollerHandle != null) {
      window.clearInterval(cur.pollerHandle);
      patchCase(set, caseId, { pollerHandle: null });
    }
    return;
  }

  const updates = await Promise.allSettled(
    activeIds.map((id) =>
      api.get<QueryStatusResponse>(`/cases/${caseId}/queries/${id}`),
    ),
  );

  const byId = new Map<number, QueryStatusResponse>();
  for (const u of updates) {
    if (u.status === "fulfilled") byId.set(u.value.query_id, u.value);
  }
  if (byId.size === 0) return;

  patchCase(set, caseId, (c) => {
    const threads = { ...c.threads };
    for (const [convIdStr, t] of Object.entries(threads)) {
      const convId = Number(convIdStr);
      let changed = false;
      const turns = t.turns.map((turn) => {
        const fresh = byId.get(turn.query_id);
        if (!fresh) return turn;
        changed = true;
        return turnFromStatus(fresh);
      });
      if (changed) threads[convId] = { ...t, turns };
    }
    return { threads };
  });
}
